import React, { useState } from "react";
import { X, DollarSign, BadgeCheck, Banknote, CheckCircle2, Loader2, Trash2, MessageSquare, Send, Lock, Link2, Mail, PlusCircle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const METHODS = [
  { value: "manual", label: "Manual / Other" },
  { value: "cash", label: "Cash" },
  { value: "check", label: "Check" },
  { value: "card", label: "Card (in person)" },
  { value: "bank_transfer", label: "Bank Transfer / Zelle" },
  { value: "stripe", label: "Stripe" },
];

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export default function ProposalPaymentModal({ proposal, onClose, onUpdated }) {
  const p = proposal;
  const total = Number(p.total) || 0;
  const paid = Number(p.amount_paid) || 0;
  const balance = Number(p.balance ?? Math.max(total - paid, 0));
  const fullyPaid = p.fully_paid || balance <= 0.01;

  const [amount, setAmount] = useState(balance > 0 ? String(balance) : "");
  const [method, setMethod] = useState("manual");
  const [note, setNote] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const addPayment = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (amt > balance + 0.01) { toast.error(`Amount exceeds the balance (${money(balance)})`); return; }
    setBusy(true);
    try {
      const updated = await api.addProposalPayment(p.id, { amount: amt, method, note: note.trim() || undefined, date });
      toast.success("Payment recorded");
      onUpdated?.(updated);
      setNote("");
      setAmount("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record payment");
    } finally {
      setBusy(false);
    }
  };

  const markAccepted = async () => {
    setAccepting(true);
    try {
      const updated = await api.adminAcceptProposal(p.id, {});
      toast.success("Marked as accepted");
      onUpdated?.(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark accepted");
    } finally {
      setAccepting(false);
    }
  };

  const [voiding, setVoiding] = useState(null);
  const voidPayment = async (index) => {
    setVoiding(index);
    try {
      const updated = await api.voidProposalPayment(p.id, index);
      toast.success("Payment voided");
      onUpdated?.(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not void payment");
    } finally {
      setVoiding(null);
    }
  };

  const [chat, setChat] = useState(p.chat || []);
  const [chatText, setChatText] = useState("");
  const [sendingChat, setSendingChat] = useState(false);
  const chatLocked = p.chat_locked || paid > 0;
  const sendChat = async () => {
    const text = chatText.trim();
    if (!text) return;
    setSendingChat(true);
    try {
      const msg = await api.postProposalChat(p.id, { text, sender: "admin" });
      setChat((c) => [...c, msg]);
      setChatText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send message");
    } finally { setSendingChat(false); }
  };

  const isAccepted = ["accepted", "paid", "deposit_paid"].includes(p.status) || fullyPaid;
  const inputCls = "w-full px-4 py-2.5 rounded-lg dark-input text-sm";
  const labelCls = "block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5";

  const payLink = `${window.location.origin}/proposal/${p.id}`;
  const [sendingLink, setSendingLink] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(payLink);
      setCopied(true);
      toast.success("Payment link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — link: " + payLink);
    }
  };
  const emailLink = async () => {
    setSendingLink(true);
    try {
      const res = await api.sendProposalPayLink(p.id);
      if (res.sent) toast.success(`Pay link emailed to ${res.to}`);
      else if (!res.to) toast.error("No client email on this proposal");
      else toast.error("Email could not be sent (check Resend config)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send email");
    } finally { setSendingLink(false); }
  };

  const [chargeDesc, setChargeDesc] = useState("");
  const [chargeAmt, setChargeAmt] = useState("");
  const [chargeBusy, setChargeBusy] = useState(false);
  const addCharge = async () => {
    const amt = Number(chargeAmt);
    if (!chargeDesc.trim()) { toast.error("Add a description"); return; }
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setChargeBusy(true);
    try {
      const updated = await api.addProposalCharge(p.id, { description: chargeDesc.trim(), amount: amt });
      toast.success("Charge added — balance reopened");
      onUpdated?.(updated);
      setChargeDesc(""); setChargeAmt("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add charge");
    } finally { setChargeBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md p-4" data-testid="proposal-payment-modal">
      <div className="glass-panel bg-[#0a0a0b]/95 rounded-3xl shadow-2xl w-full max-w-lg relative fade-in overflow-hidden border border-white/10 flex flex-col" style={{ maxHeight: "92vh" }}>
        <div className="px-6 py-4 border-b border-white/10 flex justify-between items-center bg-white/5">
          <div className="flex items-center gap-2">
            <DollarSign size={17} className="text-green-400" />
            <h3 className="text-base font-bold text-white">Manage Proposal</h3>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="proposal-payment-close"><X /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6 hide-scrollbar">
          <div>
            <div className="text-sm font-bold text-white">{p.title}</div>
            <div className="text-xs text-zinc-500 mt-0.5">{p.client_name}{p.client_contact ? ` · ${p.client_contact}` : ""}</div>
          </div>

          {/* Financial summary */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-xl bg-black/30 border border-white/10"><div className="text-[10px] text-zinc-500 uppercase font-bold">Total</div><div className="text-lg font-bold text-white" data-testid="pm-total">{money(total)}</div></div>
            <div className="p-3 rounded-xl bg-black/30 border border-white/10"><div className="text-[10px] text-zinc-500 uppercase font-bold">Paid</div><div className="text-lg font-bold text-green-400" data-testid="pm-paid">{money(paid)}</div></div>
            <div className="p-3 rounded-xl bg-black/30 border border-white/10"><div className="text-[10px] text-zinc-500 uppercase font-bold">Balance</div><div className={`text-lg font-bold ${balance > 0.01 ? "text-orange-400" : "text-white"}`} data-testid="pm-balance">{money(balance)}</div></div>
          </div>

          {/* Pay link — email + copy */}
          <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-3">
            <div className="flex items-center gap-2 text-xs font-bold text-zinc-400 uppercase tracking-wider"><Link2 size={13} /> Client Payment Link</div>
            <div className="flex items-center gap-2">
              <input readOnly value={payLink} className="flex-1 px-3 py-2 rounded-lg dark-input text-xs text-zinc-400" data-testid="pm-paylink-input" onFocus={(e) => e.target.select()} />
            </div>
            <div className="flex gap-2">
              <button onClick={copyLink} className="flex-1 py-2.5 rounded-lg bg-white/10 text-white text-sm font-bold hover:bg-white/20 transition flex items-center justify-center" data-testid="pm-copy-link-btn">
                <Link2 size={15} className="mr-2" /> {copied ? "Copied!" : "Copy Link"}
              </button>
              <button onClick={emailLink} disabled={sendingLink} className="flex-1 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-bold hover:bg-primary-500 transition disabled:opacity-60 flex items-center justify-center" data-testid="pm-email-link-btn">
                {sendingLink ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Mail size={15} className="mr-2" />} Email Pay Link
              </button>
            </div>
          </div>

          {/* Status / accept */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-2">
              <BadgeCheck size={16} className={isAccepted ? "text-green-400" : "text-zinc-500"} />
              <span className="text-sm text-zinc-300">Status: <span className="font-bold text-white capitalize">{(p.status || "draft").replace("_", " ")}</span></span>
            </div>
            {!isAccepted && (
              <button onClick={markAccepted} disabled={accepting} className="px-3 py-1.5 rounded-lg bg-primary-600 text-white text-xs font-bold hover:bg-primary-500 transition disabled:opacity-60 flex items-center" data-testid="mark-accepted-btn">
                {accepting ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <BadgeCheck size={13} className="mr-1.5" />} Mark Accepted
              </button>
            )}
          </div>

          {/* Add payment */}
          {fullyPaid ? (
            <div className="text-center py-4 rounded-xl bg-green-500/5 border border-green-500/20">
              <CheckCircle2 size={32} className="text-green-400 mx-auto mb-2" />
              <div className="text-sm font-bold text-white">Paid in Full</div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-xs font-bold text-zinc-400 uppercase tracking-wider"><Banknote size={14} /> Record a Payment</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Amount</label>
                  <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" max={balance} className={inputCls} data-testid="pm-amount-input" />
                </div>
                <div>
                  <label className={labelCls}>Date</label>
                  <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} data-testid="pm-date-input" />
                </div>
              </div>
              <div>
                <label className={labelCls}>Method</label>
                <select value={method} onChange={(e) => setMethod(e.target.value)} className={inputCls} data-testid="pm-method-select">
                  {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Note (optional)</label>
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Photoshoot deposit received via Zelle" className={inputCls} data-testid="pm-note-input" />
              </div>
              <div className="flex gap-2">
                <button onClick={() => setAmount(String(balance))} className="px-3 py-2 rounded-lg bg-white/5 text-zinc-300 text-xs font-bold hover:bg-white/10 transition" data-testid="pm-fill-balance">Full balance ({money(balance)})</button>
                <button onClick={addPayment} disabled={busy} className="flex-1 py-2.5 rounded-lg bg-green-600 text-white text-sm font-bold hover:bg-green-500 transition disabled:opacity-60 flex items-center justify-center" data-testid="pm-record-btn">
                  {busy ? <Loader2 size={15} className="mr-2 animate-spin" /> : <DollarSign size={15} className="mr-2" />} Record Payment
                </button>
              </div>
            </div>
          )}

          {/* Add a charge (reopens balance) */}
          {isAccepted && (
            <div className="space-y-3 p-4 rounded-xl bg-black/20 border border-white/10">
              <div className="flex items-center gap-2 text-xs font-bold text-zinc-400 uppercase tracking-wider"><PlusCircle size={14} /> Add a Charge</div>
              <p className="text-[11px] text-zinc-500">Add an extra line item (e.g. an add-on or overage). This increases the total and reopens a balance the client can pay.</p>
              <input value={chargeDesc} onChange={(e) => setChargeDesc(e.target.value)} placeholder="Description — e.g. Extra hour of coverage" className={inputCls} data-testid="pm-charge-desc" />
              <div className="flex gap-2">
                <input type="number" value={chargeAmt} onChange={(e) => setChargeAmt(e.target.value)} placeholder="Amount" className={inputCls} data-testid="pm-charge-amount" />
                <button onClick={addCharge} disabled={chargeBusy} className="px-4 rounded-lg bg-primary-600 text-white text-sm font-bold hover:bg-primary-500 transition disabled:opacity-60 flex items-center whitespace-nowrap" data-testid="pm-charge-add">
                  {chargeBusy ? <Loader2 size={15} className="mr-2 animate-spin" /> : <PlusCircle size={15} className="mr-2" />} Add Charge
                </button>
              </div>
            </div>
          )}

          {/* Payment history */}
          {(p.payments || []).length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Payment History</div>
              {(p.payments || []).map((pay, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg bg-black/20 border border-white/5" data-testid="pm-history-row">
                  <div>
                    <div className="text-sm font-bold text-white">{money(pay.amount)}</div>
                    <div className="text-[11px] text-zinc-500">{(pay.method || "manual").replace("_", " ")}{pay.note ? ` · ${pay.note}` : ""}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-[11px] text-zinc-500">{(pay.at || "").slice(0, 10)}{pay.manual ? "" : " · online"}</div>
                    <button onClick={() => voidPayment(i)} disabled={voiding === i} title="Void this payment" className="p-1.5 rounded-lg bg-white/5 text-zinc-400 hover:bg-red-500/20 hover:text-red-400 transition disabled:opacity-50" data-testid="pm-void-btn">
                      {voiding === i ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Discussion with client */}
          <div className="space-y-3">
            <div className="text-xs font-bold text-zinc-400 uppercase tracking-wider flex items-center gap-2"><MessageSquare size={13} /> Client Discussion</div>
            <div className="space-y-2 max-h-56 overflow-y-auto pr-1 hide-scrollbar">
              {chat.length === 0 && <p className="text-xs text-zinc-600">No messages yet.</p>}
              {chat.map((m) => (
                <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] px-3 py-2 rounded-xl text-sm ${m.sender === "admin" ? "bg-primary-600 text-white rounded-br-sm" : "bg-white/10 text-zinc-100 rounded-bl-sm"}`}>
                    <div className="text-[9px] font-bold uppercase tracking-wider opacity-70 mb-0.5">{m.sender === "admin" ? "You" : (p.client_contact || "Client")}</div>
                    {m.text}
                  </div>
                </div>
              ))}
            </div>
            {chatLocked ? (
              <div className="flex items-center gap-2 text-xs text-zinc-500 bg-white/5 rounded-lg px-3 py-2"><Lock size={12} /> Locked — deal confirmed, conversation continues in the workroom.</div>
            ) : (
              <div className="flex gap-2">
                <input value={chatText} onChange={(e) => setChatText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") sendChat(); }} placeholder="Reply to the client…" className="flex-1 px-3 py-2 rounded-lg dark-input text-sm" data-testid="pm-chat-input" />
                <button onClick={sendChat} disabled={sendingChat} className="px-3 py-2 bg-primary-600 text-white rounded-lg font-bold hover:bg-primary-500 transition disabled:opacity-60 flex items-center" data-testid="pm-chat-send">{sendingChat ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}</button>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-white/10 flex justify-end bg-white/5">
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl bg-white/5 text-zinc-300 text-sm font-bold hover:bg-white/10 transition" data-testid="proposal-payment-done">Done</button>
        </div>
      </div>
    </div>
  );
}
